import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Search, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const getImageUrl = (photoPath) => {
  if (!photoPath) return 'https://images.pexels.com/photos/1040881/pexels-photo-1040881.jpeg?auto=compress&cs=tinysrgb&w=100';
  if (photoPath.startsWith('http')) return photoPath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${photoPath}`;
};

const Messages = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const { token } = useAuth();

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
    } catch (err) {
      toast.error('Kunne ikke indlæse beskeder');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const filteredConversations = conversations.filter(conv =>
    conv.user.name.toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'I går';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('da-DK', { weekday: 'short' });
    }
    return date.toLocaleDateString('da-DK', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="w-6 h-6 text-primary" />
            <span className="text-lg font-display font-semibold text-foreground">Beskeder</span>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Søg i samtaler..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 rounded-full bg-muted border-none"
              data-testid="search-messages-input"
            />
          </div>
        </div>
      </div>

      <div className="pt-32 px-4">
        <div className="max-w-lg mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-display font-semibold text-foreground">
                {search ? 'Ingen samtaler fundet' : 'Ingen beskeder endnu'}
              </h3>
              <p className="text-muted-foreground mt-2 max-w-xs mx-auto">
                {search ? 'Prøv et andet søgeord' : 'Match med nogen for at starte en samtale!'}
              </p>
              {!search && (
                <Link to="/discover">
                  <button className="mt-6 btn-primary" data-testid="find-matches-btn">
                    Find Matches
                  </button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredConversations.map((conv, index) => (
                <motion.div
                  key={conv.user.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Link to={`/chat/${conv.user.id}`}>
                    <div
                      className={`flex items-center gap-4 p-4 rounded-2xl transition-colors hover:bg-muted/50 ${
                        conv.unread_count > 0 ? 'bg-primary/5' : ''
                      }`}
                      data-testid={`conversation-${conv.user.id}`}
                    >
                      <div className="relative">
                        <img
                          src={getImageUrl(conv.user.photos?.[0])}
                          alt={conv.user.name}
                          className="w-14 h-14 rounded-full object-cover"
                        />
                        {conv.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white">{conv.unread_count}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className={`font-semibold truncate ${conv.unread_count > 0 ? 'text-foreground' : 'text-foreground'}`}>
                            {conv.user.name}
                          </h4>
                          <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                            {conv.last_message && formatTime(conv.last_message.created_at)}
                          </span>
                        </div>
                        <p className={`text-sm truncate mt-0.5 ${
                          conv.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'
                        }`}>
                          {conv.last_message?.content || 'Ingen beskeder endnu'}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Messages;
